import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import api from '../../API/api';

export const submitKyc = createAsyncThunk('kyc/submit', async (formData, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/kyc', formData); // BASE_URL must end with /api
    return data.request;
  } catch (e) {
    const status = e?.response?.status;
    const code = e?.response?.data?.code;
    if (status === 409 && code === 'KYC_PENDING') {
      return rejectWithValue('Your verification is already under review. Please wait up to 24 hours.');
    }
    return rejectWithValue(e?.response?.data?.message || e.message);
  }
});

export const fetchMyKyc = createAsyncThunk('kyc/mine', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/kyc/mine');
    return data.request || null;
  } catch (e) {
    return rejectWithValue(e?.response?.data?.message || e.message);
  }
});

export const fetchPendingKyc = createAsyncThunk('kyc/pending', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/kyc/pending');
    return data.requests || [];
  } catch (e) {
    return rejectWithValue(e?.response?.data?.message || e.message);
  }
});

export const approveKyc = createAsyncThunk('kyc/approve', async (id, { rejectWithValue }) => {
  try {
    const { data } = await api.patch(`/kyc/${id}/approve`);
    return data.request;
  } catch (e) {
    return rejectWithValue(e?.response?.data?.message || e.message);
  }
});

export const rejectKyc = createAsyncThunk('kyc/reject', async ({ id, reason }, { rejectWithValue }) => {
  try {
    const { data } = await api.patch(`/kyc/${id}/reject`, { reason });
    return data.request;
  } catch (e) {
    return rejectWithValue(e?.response?.data?.message || e.message);
  }
});

const initialState = { mine: null, pending: [], loading: false, error: null, actionLoading: false };

const slice = createSlice({
  name: 'kyc',
  initialState,
  reducers: { clearKycError: (s) => { s.error = null; } },
  extraReducers: (b) => {
    b
      .addCase(submitKyc.pending,  (s)=>{ s.loading=true; s.error=null; })
      .addCase(submitKyc.fulfilled,(s,a)=>{ s.loading=false; s.mine=a.payload; })
      .addCase(submitKyc.rejected, (s,a)=>{ s.loading=false; s.error=a.payload; })

      .addCase(fetchMyKyc.pending,  (s)=>{ s.loading=true; s.error=null; })
      .addCase(fetchMyKyc.fulfilled,(s,a)=>{ s.loading=false; s.mine=a.payload; })
      .addCase(fetchMyKyc.rejected, (s,a)=>{ s.loading=false; s.error=a.payload; })

      .addCase(fetchPendingKyc.pending,  (s)=>{ s.loading=true; s.error=null; })
      .addCase(fetchPendingKyc.fulfilled,(s,a)=>{ s.loading=false; s.pending=a.payload; })
      .addCase(fetchPendingKyc.rejected, (s,a)=>{ s.loading=false; s.error=a.payload; })

      .addCase(approveKyc.pending,  (s)=>{ s.actionLoading=true; s.error=null; })
      .addCase(approveKyc.fulfilled,(s,a)=>{ s.actionLoading=false; s.pending = s.pending.filter(x=>String(x._id)!==String(a.payload._id)); })
      .addCase(approveKyc.rejected, (s,a)=>{ s.actionLoading=false; s.error=a.payload; })

      .addCase(rejectKyc.pending,  (s)=>{ s.actionLoading=true; s.error=null; })
      .addCase(rejectKyc.fulfilled,(s,a)=>{ s.actionLoading=false; s.pending = s.pending.filter(x=>String(x._id)!==String(a.payload._id)); })
      .addCase(rejectKyc.rejected, (s,a)=>{ s.actionLoading=false; s.error=a.payload; });
  }
});

export const { clearKycError } = slice.actions;
export default slice.reducer;

const selectKyc = (s)=> s.kyc || initialState;
export const selectMyKyc = createSelector([selectKyc], s=>s.mine);
export const selectPendingKyc = createSelector([selectKyc], s=>s.pending);
export const selectKycLoading = createSelector([selectKyc], s=>s.loading || s.actionLoading);
export const selectKycError   = createSelector([selectKyc], s=>s.error);
